import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, GripVertical, Play, Pause, Download, Clock, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { SignedAudio, signedUrl, openDownload, retrySigned } from "@/lib/media";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const PRESETS = [
  { id: "together", title: "A. Together by person", text: "Each message is followed by its reply — e.g. Nana to Freddie, Freddie to Nana, then the next person." },
  { id: "outgoing_first", title: "B. All messages, then all replies", text: "All of the storyteller's messages first, followed by everyone's replies." },
];

function useSequencePlayer() {
  const audio = useRef(null);
  const [now, setNow] = useState(null);
  const [mode, setMode] = useState(null);
  useEffect(() => () => audio.current?.pause(), []);
  const stop = () => { audio.current?.pause(); setNow(null); setMode(null); };
  const playAt = (list, i, m) => {
    if (i >= list.length) return stop();
    audio.current?.pause();
    const a = new Audio();
    audio.current = a;
    a.onended = () => (m === "all" ? playAt(list, i + 1, m) : stop());
    const fail = () => { toast.error("That recording couldn't be played. Please try again."); stop(); };
    a.onerror = () => { if (audio.current === a) retrySigned(a, list[i].audio_path, fail); };
    a.onplaying = () => { if (audio.current === a) { setNow(list[i].id); setMode(m); } };
    signedUrl(list[i].audio_path).then((u) => {
      if (audio.current !== a) return;
      a.src = u;
      a.play().then(() => { setNow(list[i].id); setMode(m); }).catch(() => retrySigned(a, list[i].audio_path, fail));
    }).catch(() => { toast.error("That recording couldn't be played. Please try again."); stop(); });
  };
  return { now, mode, stop, playOne: (r) => (now === r.id ? stop() : playAt([r], 0, "one")), playAll: (list) => (mode === "all" ? stop() : playAt(list, 0, "all")) };
}

function Row({ r, i, last, playing, onPlay, onMove, onDragStart, onDrop }) {
  return (
    <li draggable onDragStart={() => onDragStart(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
      className={`glass-card p-4 flex items-center gap-3 ${playing ? "ring-2 ring-[#8B2CFF]" : ""}`} data-testid={`order-item-${i}`} data-id={r.id}>
      <GripVertical className="h-5 w-5 text-[#5E5670] cursor-grab shrink-0 hidden sm:block" aria-hidden="true" />
      <span className="font-serif text-xl text-[#B57BFF] w-7 text-center">{i + 1}</span>
      <button onClick={onPlay} aria-label={`${playing ? "Stop" : "Play"} ${r.speaker} to ${r.recipient}`} data-testid={`order-play-${i}`}
        className="h-11 w-11 rounded-full ghost-gold flex items-center justify-center shrink-0">
        {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-[#EDE6F7] text-lg truncate" data-testid={`order-label-${i}`}>{r.speaker} → {r.recipient}</p>
        <p className="text-sm text-[#A49CB8]">{r.direction === "outgoing" ? "Message" : "Reply"} · {fmt(r.duration)}</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-1">
        <button disabled={i === 0} onClick={() => onMove(i, -1)} aria-label={`Move ${r.speaker} to ${r.recipient} earlier`} data-testid={`order-up-${i}`}
          className="h-10 w-10 rounded-full border border-[#33264A] text-[#C9C2D8] disabled:opacity-30 flex items-center justify-center"><ArrowUp className="h-4 w-4" /></button>
        <button disabled={last} onClick={() => onMove(i, 1)} aria-label={`Move ${r.speaker} to ${r.recipient} later`} data-testid={`order-down-${i}`}
          className="h-10 w-10 rounded-full border border-[#33264A] text-[#C9C2D8] disabled:opacity-30 flex items-center justify-center"><ArrowDown className="h-4 w-4" /></button>
      </div>
    </li>
  );
}

function FinalPanel({ plan }) {
  const f = plan.final;
  if (!f) return null;
  return (
    <section className="glass-card p-6 sm:p-8 mb-8" data-testid="final-keepsake">
      <p className="eyebrow mb-2">Your audio keepsake</p>
      <h2 className="font-serif text-3xl text-[#EDE6F7] mb-2">Ready to listen</h2>
      <p className="text-[#B9B3C8] mb-4">{f.items.length} original recordings · {fmt(f.seconds)} · created {new Date(f.created_at).toLocaleString("en-GB")}</p>
      {f.outdated && <p className="mb-4 text-[#E6CCF2]" role="status" data-testid="final-outdated">Your recordings or order have changed since this file was made. Create it again to include the changes.</p>}
      {f.incomplete && <p className="mb-4 text-[#D9C6FF]" data-testid="final-incomplete">Created with the recordings available. Not included: {f.missing.join(", ")}.</p>}
      <SignedAudio controls path={f.path} className="w-full mb-4" data-testid="final-audio" />
      <button type="button" onClick={() => openDownload("keepsake").catch((e) => toast.error(apiError(e)))} data-testid="final-download-btn" className="gold-btn inline-flex items-center h-14 px-7 rounded-full text-lg"><Download className="h-5 w-5 mr-2" /> Download MP3</button>
    </section>
  );
}

export default function KeepsakeBuilder() {
  const [plan, setPlan] = useState(null);
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);
  const player = useSequencePlayer();

  const apply = (p) => { setPlan(p); setItems(p.sequence); setDirty(false); };
  const load = () => api.get("/keepsake/plan").then((r) => apply(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const processing = plan?.job?.status === "processing";
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(() => api.get("/keepsake/plan").then((r) => { setPlan(r.data); if (r.data.job?.status !== "processing") { apply(r.data); if (r.data.job?.status === "done") toast.success("Your keepsake is ready."); } }), 3000);
    return () => clearInterval(t);
  }, [processing]);

  const choose = async (preset) => {
    player.stop();
    try { apply((await api.put("/keepsake/order", { preset })).data); toast.success("Playback order saved."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const move = (i, d) => { const n = [...items]; [n[i], n[i + d]] = [n[i + d], n[i]]; setItems(n); setDirty(true); };
  const dropAt = (to) => { const from = drag.current; if (from == null || from === to) return; const n = [...items]; const [x] = n.splice(from, 1); n.splice(to, 0, x); setItems(n); setDirty(true); };
  const saveCustom = async () => {
    try { apply((await api.put("/keepsake/order", { preset: "custom", items: items.map((r) => r.id) })).data); toast.success("Your custom order is saved."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const finalise = async (confirmIncomplete) => {
    setBusy(true);
    try {
      const { data } = await api.post("/keepsake/finalise", { confirm_incomplete: confirmIncomplete });
      setConfirm(false);
      if (data.reused) toast.success("Your keepsake is already up to date.");
      await load();
    } catch (e) {
      if (e?.response?.status === 409) setConfirm(true); else toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  if (!plan) return <div className="min-h-screen legacy-bg"><Navbar /><p className="text-center text-[#B9B3C8] pt-24" role="status">Opening your recordings…</p></div>;
  const total = items.reduce((a, r) => a + r.duration, 0);

  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/dashboard" className="text-[#C9C2D8] underline underline-offset-4" data-testid="builder-back">← Back to your family</Link>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#EDE6F7] mt-4 mb-2">Your audio keepsake</h1>
        <p className="text-lg text-[#B9B3C8] mb-8">Choose the order, preview it, then create one MP3 from your family's original recordings. Nothing is rewritten or replaced.</p>

        <FinalPanel plan={plan} />

        <section className="mb-8" aria-labelledby="order-h">
          <h2 id="order-h" className="eyebrow mb-3">Playback order</h2>
          <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Playback order">
            {PRESETS.map((p) => (
              <button key={p.id} role="radio" aria-checked={plan.preset === p.id && !dirty} onClick={() => choose(p.id)} data-testid={`preset-${p.id}`}
                className={`text-left rounded-xl border p-4 transition-colors ${plan.preset === p.id && !dirty ? "border-[#8B2CFF] bg-[#1E1428]" : "border-[#33264A] bg-[#1A1222] hover:border-[#5E5670]"}`}>
                <span className="block text-[#EDE6F7] font-semibold">{p.title}</span>
                <span className="block text-sm text-[#A49CB8] mt-1">{p.text}</span>
              </button>
            ))}
          </div>
          <p className="text-sm text-[#8C84A0] mt-3" data-testid="order-status">
            {dirty ? "You've changed the order — save it to keep it." : plan.preset === "custom" ? "Using your own custom order." : "Or drag recordings, or use the arrows, to set your own order."}
          </p>
        </section>

        {items.length === 0 ? (
          <p className="glass-card p-6 text-[#B9B3C8]" data-testid="no-recordings">No recordings yet. Record a message to someone in your family to get started.</p>
        ) : (
          <ol className="space-y-3 mb-4" data-testid="order-list">
            {items.map((r, i) => (
              <Row key={r.id} r={r} i={i} last={i === items.length - 1} playing={player.now === r.id}
                onPlay={() => player.playOne(r)} onMove={move} onDragStart={(x) => (drag.current = x)} onDrop={dropAt} />
            ))}
          </ol>
        )}
        <div className="flex flex-wrap gap-3 mb-10">
          {dirty && <button onClick={saveCustom} data-testid="save-order-btn" className="gold-btn h-12 px-6 rounded-full">Save this order</button>}
          {items.length > 0 && (
            <button onClick={() => player.playAll(items)} data-testid="preview-sequence-btn" className="ghost-gold h-12 px-6 rounded-full flex items-center">
              {player.mode === "all" ? <><Pause className="h-4 w-4 mr-2" /> Stop preview</> : <><Play className="h-4 w-4 mr-2" /> Preview the whole sequence · {fmt(total)}</>}
            </button>
          )}
        </div>

        <section className="glass-card p-6 mb-8" data-testid="missing-panel">
          <h2 className="font-serif text-2xl text-[#EDE6F7] mb-3">{plan.missing.length ? "Still to come" : "Everything is here"}</h2>
          {plan.missing.length ? (
            <ul className="space-y-2">{plan.missing.map((m) => <li key={m.member_id + m.kind} className="flex gap-2 text-[#C9C2D8]"><Clock className="h-5 w-5 text-[#5E5670] shrink-0" />{m.label}</li>)}</ul>
          ) : <p className="flex gap-2 text-[#D9C6FF]"><CheckCircle2 className="h-5 w-5" /> Every message and reply has been recorded.</p>}
          <p className="text-sm text-[#8C84A0] mt-3">Invitations that have been sent but not answered are not counted as complete.</p>
        </section>

        {processing ? (
          <div className="glass-card p-6 text-center" role="status" data-testid="build-progress">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-[#B57BFF] mb-3" />
            <p className="text-[#EDE6F7] text-lg">Joining recording {plan.job.progress} of {plan.job.total}…</p>
            <p className="text-sm text-[#A49CB8]">You can leave this page — we'll email you when it's ready.</p>
          </div>
        ) : (
          <>
            {plan.job?.status === "failed" && <p className="mb-4 flex gap-2 text-[#E6CCF2]" role="alert" data-testid="build-error"><AlertCircle className="h-5 w-5" />{plan.job.error}</p>}
            {confirm && (
              <div className="glass-card p-6 mb-4 border border-[rgba(139,44,255,0.4)]" data-testid="confirm-incomplete">
                <p className="text-[#EDE6F7] text-lg mb-2">Create your keepsake with the recordings you have?</p>
                <p className="text-[#B9B3C8] mb-4">It will contain {items.length} recording{items.length !== 1 ? "s" : ""} ({fmt(total)}). Not yet recorded: {plan.missing.map((m) => m.label).join(", ")}. You can create it again later if more arrive.</p>
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => finalise(true)} disabled={busy} data-testid="confirm-incomplete-btn" className="gold-btn h-12 px-6 rounded-full">Yes, create it now</button>
                  <button onClick={() => setConfirm(false)} data-testid="cancel-incomplete-btn" className="ghost-gold h-12 px-6 rounded-full">Wait for the rest</button>
                </div>
              </div>
            )}
            <button onClick={() => finalise(false)} disabled={busy || dirty || items.length === 0} data-testid="finalise-btn"
              className="gold-btn w-full h-16 rounded-full text-xl disabled:opacity-50">
              {dirty ? "Save your order first" : plan.final && !plan.final.outdated ? "Your keepsake is up to date" : "Create my audio keepsake"}
            </button>
          </>
        )}
      </main>
    </div>
  );
}
